import { useEffect, useMemo, useRef } from 'react';
import { useChatStore, useChatValidationStore } from 'src/hooks/chat';
import { ChatMessage, ToolCall, ToolResponse } from 'src/api';
import {
  Button,
  ChatLayout,
  ChatLayoutActionRenderer,
  ChatLayoutContentRenderer,
  Logo,
  ThreeDotsLoader,
} from '@insight-health-ai/roseate/components';
import { RetweetOutlined, RobotOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import { Tooltip } from 'antd';
import { useAuthStore } from 'src/hooks/auth';
import { Progress } from 'src/components';
import { SpeakerIcon, SpeakerMutedIcon } from 'src/icons';
import { useTheme } from '@insight-health-ai/roseate/theme';
import { VCALoader } from './loader';
import { Forms } from './forms';
import { MessageContent, MessageTools, MessageToolResults } from './message';
import { ChatControls, ChatControlsRef } from './chatControls';
import { LikeOutlined, DislikeOutlined, EditOutlined } from '@ant-design/icons';
import { CompletionScreen } from './completionScreen';
import { analytics } from 'src/services/analytics';
import {
  getAppointmentDetailsForAnalytics,
  isFromCareConnect,
} from 'src/utils';
import { useTextToSpeech } from 'src/hooks/common';
import { toast } from '@insight-health-ai/roseate/components/toast';
import { useFormStore } from 'src/hooks/form';
import { useTranslation } from 'react-i18next';
import { i18n } from 'src/services/i18n';

type ParsedChatMessage = Omit<ChatMessage, 'timestamp' | 'trace_id'> & {
  timestamp: Date;
  id: string;
};

type ToolPool = Record<
  ToolCall['id'],
  { call?: ToolCall; response?: ToolResponse }
>;

const parseToolResponsesFromMessages = (
  messages: ChatMessage[]
): {
  chatMessages: ParsedChatMessage[];
  toolPool: ToolPool;
} => {
  const toolPool = (messages || []).reduce((acc, message) => {
    const { tool_calls, tool_responses } = message;
    tool_calls?.forEach((call) => {
      acc[call.id] = { call };
    });
    tool_responses?.forEach((response) => {
      acc[response.id] = {
        ...(acc[response.id] || {}),
        response,
      };
    });
    return acc;
  }, {} as ToolPool);
  const chatMessages = messages.reduce((acc, message, index) => {
    const { role, content, tool_calls, tool_responses, timestamp, trace_id } =
      message;
    const toolCalls = tool_calls?.filter(
      (call) => !toolPool[call.id]?.response
    );
    const toolResponses = tool_responses?.filter(
      (response) => toolPool[response.id]?.call
    );
    if (
      ['assistant', 'user'].includes(role) &&
      (toolCalls?.length || toolResponses?.length || content)
    ) {
      acc.push({
        role: role as 'assistant' | 'user',
        content,
        timestamp:
          timestamp.includes('UTC') || timestamp.includes('Z')
            ? new Date(timestamp)
            : new Date(`${(timestamp || '').replace(' ', 'T')}Z`),
        id: `${trace_id}_${index}`,
        tool_calls: toolCalls || [],
        tool_responses: toolResponses || [],
      });
    }
    return acc;
  }, [] as ParsedChatMessage[]);
  return { chatMessages, toolPool };
};

const ShimmerText = styled.div`
  color: #555555;
  background: linear-gradient(
    to right,
    #555555 0%,
    #555555 10%,
    #aaaaaa 20%,
    #aaaaaa 30%,
    #555555 40%,
    #555555 100%
  );
  background-size: 300% auto;
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: shimmer 6s linear infinite;

  @keyframes shimmer {
    0% {
      background-position: 100% center;
    }
    100% {
      background-position: -200% center;
    }
  }
`;

const ChatContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  height: 100%;
  overflow: hidden;
  background: linear-gradient(
    to bottom right,
    #c9f5e7 4%,
    #c9f5e7 0%,
    #c9f5e7 5%,
    #ddfaf9 11%,
    #ddfaf9 10%,
    #ffffff 39.5%,
    #ffffff 55.5%,
    #ddfaf9 100%,
    #ddfaf9 10%
  );
`;

const LOADER_MESSAGE_ID = 'loader';
const ERROR_MESSAGE_ID = 'error';

const VCA: React.FC = () => {
  const chatToken = useChatValidationStore.getState().token!;
  const chatControlsRef = useRef<ChatControlsRef>(null);

  const { t } = useTranslation('vca');

  const { theme } = useTheme();
  const { chat: chatLinkState } = useChatValidationStore();
  const { appointment } = useAuthStore();
  const { getNextForm, loading: loadingForm } = useFormStore();
  const {
    progress,
    chat,
    loading,
    error,
    createChat,
    getChatHistory,
    sendResponse,
    sendChatFeedback,
    complete,
    regenerateResponse,
    textToSpeech,
    setTextToSpeech,
    zoom,
  } = useChatStore();
  const uuid = appointment?.chat_uuid!;

  const { chatMessages, toolPool } = useMemo(
    () => parseToolResponsesFromMessages(chat.messages),
    [chat.messages]
  );

  const chatMessagesWithLoader = useMemo(() => {
    if (chat.loading) {
      return [
        ...chatMessages,
        {
          id: LOADER_MESSAGE_ID,
          role: 'assistant' as const,
          content: chat.loading,
          timestamp: new Date(),
        },
      ];
    }
    if (chat.error || error) {
      return [
        ...chatMessages,
        {
          id: ERROR_MESSAGE_ID,
          role: 'assistant' as const,
          content: t('responseError'),
          timestamp: new Date(),
        },
      ];
    }

    return chatMessages;
  }, [chatMessages, chat.loading, chat.error, error]);

  const lastUserMessage = useMemo(() => {
    return chatMessages
      .filter((message) => message.role === 'user')
      .slice(-1)[0];
  }, [chatMessages]);

  const lastAssistantMessage = useMemo(() => {
    return chatMessages
      .filter((message) => message.role === 'assistant')
      .slice(-1)[0];
  }, [chatMessages]);

  const chatRoles = {
    assistant: {
      label: 'Assistant',
      backgroundColor: theme.colors.gray[200],
      align: 'left',
      avatar: {
        text: <Logo compact className="h-7" />,
        style: {
          backgroundColor: '#fff',
          boxShadow: '0 4px 8px rgba(6, 5, 5, 0.32)',
        },
      },
    },
    user: {
      label: [
        appointment?.patient_first_name,
        appointment?.patient_last_name,
      ].join(' '),
      align: 'right',
      avatar: {
        style: {
          boxShadow: '0 4px 8px rgba(6, 5, 5, 0.32)',
        },
      },
    },
  } as const;

  const handleSendMessage = (message: string) => {
    analytics.track(
      'Sent Message',
      getAppointmentDetailsForAnalytics(appointment!)
    );
    sendResponse({
      content: message,
      timestamp: new Date().toISOString(),
      chatToken,
      uuid,
    });
  };

  const initChat = async () => {
    if ((appointment?.chat_completion_status || '').toLowerCase() === 'new') {
      createChat({
        chatToken,
      });
    } else {
      getChatHistory({
        chatToken,
      });
    }
  };

  const handleFailure = async () => {
    analytics.track(
      'Retry Link Clicked',
      getAppointmentDetailsForAnalytics(appointment!)
    );
    if (error) {
      await initChat();
    } else {
      await regenerateResponse({
        chatToken,
        uuid,
      });
    }
  };

  const handleToolResponses = async (responses: ToolResponse[]) => {
    await sendResponse({
      content: '',
      timestamp: new Date().toISOString(),
      chatToken,
      uuid,
      toolResponses: responses,
    });
  };

  useEffect(() => {
    initChat();
  }, []);

  useEffect(() => {
    if (progress >= 100) {
      if (isFromCareConnect()) {
        if (chatLinkState?.patientLogoutStatus) {
          toast.info(t('completionMessage'));
        }
        return;
      }

      if (
        ['Summary InProgress', 'Completed'].includes(
          appointment?.chat_completion_status || ''
        )
      ) {
        useChatStore.setState({ complete: true });
        return;
      }

      getNextForm();
    }
  }, [progress]);

  const llmThinking = loading || error || chat.loading || chat.error;

  const textToSpeechEnabled = textToSpeech !== 'off';
  const { read, stop } = useTextToSpeech({
    language: appointment?.patient_preferred_language || i18n.language,
    enabled: textToSpeechEnabled,
    onError: () => {
      toast.error(t('textToSpeech.error'));
      setTextToSpeech('off');
    },
  });

  useEffect(() => {
    const text = llmThinking ? '' : lastAssistantMessage?.content;

    if (textToSpeechEnabled && text) {
      read(text);
    } else {
      stop();
    }
  }, [textToSpeechEnabled, llmThinking, lastAssistantMessage?.content]);

  const actionRenderer: ChatLayoutActionRenderer<keyof typeof chatRoles> = ({
    role,
    message,
  }) => {
    const parsedMessage = message as ParsedChatMessage;

    if (parsedMessage.tool_calls?.length) {
      return (
        <MessageTools
          toolCalls={parsedMessage.tool_calls}
          sendResponse={handleToolResponses}
        />
      );
    }

    if (parsedMessage.tool_responses?.length) {
      return (
        <MessageToolResults toolResponses={parsedMessage.tool_responses} />
      );
    }

    const sendFeedback = (score: 0 | 1) =>
      sendChatFeedback({
        chatToken,
        score,
        traceId: message.id!,
      });
    const { label } = role;
    if (label === 'Assistant') {
      return (
        <div className="flex gap-2 pt-2 opacity-0 hover:opacity-100 w-full">
          <LikeOutlined
            className="cursor-pointer hover:text-green-500 text-lg"
            onClick={() => {
              analytics.track('Feedback Voted Lumi', {
                ...getAppointmentDetailsForAnalytics(appointment!),
                thumbs: 'Up',
              });
              sendFeedback(1);
            }}
          />
          <DislikeOutlined
            className="cursor-pointer hover:text-red-500 text-lg"
            onClick={() => {
              analytics.track('Feedback Voted Lumi', {
                ...getAppointmentDetailsForAnalytics(appointment!),
                thumbs: 'Down',
              });
              sendFeedback(0);
            }}
          />
        </div>
      );
    }

    if (message.id === lastUserMessage?.id) {
      return (
        <div className="pt-2">
          <Button
            shape="round"
            size="sm"
            variant="outlined"
            onClick={() => {
              const editMessagePrefix = 'Edited Message: ';
              analytics.track('Clicked Edit Button In Lumi Chat', {
                ...getAppointmentDetailsForAnalytics(appointment!),
              });
              chatControlsRef.current?.forceSetMessage(
                `${editMessagePrefix}${
                  (message.content as string).startsWith?.(editMessagePrefix)
                    ? (message.content as string).slice(
                        editMessagePrefix.length
                      )
                    : (message.content as string)
                }`
              );
            }}
            icon={<EditOutlined />}
          >
            {t('edit')}
          </Button>
        </div>
      );
    }

    return null;
  };

  const messageRenderer: ChatLayoutContentRenderer<keyof typeof chatRoles> = ({
    content,
    message,
  }) => {
    if (message.id === LOADER_MESSAGE_ID) {
      return typeof content === 'boolean' ? (
        <ThreeDotsLoader size="lg" />
      ) : (
        <ShimmerText className="flex gap-2 items-center pb-2">
          {content}
          <ThreeDotsLoader size="sm" />
        </ShimmerText>
      );
    }
    if (message.id === ERROR_MESSAGE_ID) {
      return (
        <div className="flex flex-col items-center justify-center gap-2 flex-0 p-2">
          <div className="text-danger flex flex-col items-center justify-center gap-2">
            <RobotOutlined className="text-4xl" />
            {message.content}
          </div>
          <Button
            icon={<RetweetOutlined />}
            variant="outlined"
            onClick={handleFailure}
          >
            {t('regenerateResponse')}
          </Button>
        </div>
      );
    }
    if (!message.content) {
      if ((message as any).tool_calls?.length) {
        return null;
      }
      return <></>;
    }
    return (
      <MessageContent
        message={message as ChatMessage}
        sendResponse={handleToolResponses}
      />
    );
  };

  return (
    <div id="vca-container" className="flex flex-col overflow-hidden h-full">
      <div className="flex gap-2 py-2 px-2 sm:px-4 md:px-8 flex-0 w-full overflow-visible shadow border-b border-gray-100 z-10">
        {/* @todo: Harshit | Replace with the component from roseate */}
        <Tooltip
          title={
            textToSpeech === 'on'
              ? t('textToSpeech.onLabel')
              : t('textToSpeech.offLabel')
          }
        >
          <div
            id="vca-tts-icon-container"
            className="flex-0 flex items-center cursor-pointer"
            onClick={() => {
              analytics.track('Voice Icon Clicked Lumi Chat', {
                ...getAppointmentDetailsForAnalytics(appointment!),
                is_voice_enabled: textToSpeech ? 'False' : 'True',
              });
              setTextToSpeech(textToSpeech === 'on' ? 'off' : 'on');
            }}
          >
            {textToSpeech === 'on' ? (
              <SpeakerIcon height={20} width={20} />
            ) : (
              <SpeakerMutedIcon height={20} width={20} />
            )}
          </div>
        </Tooltip>
        <div
          id="vca-progress-container"
          className="flex-0 flex items-center gap-2 w-full"
        >
          <div className="text-sm">{t('progress')}: </div>
          {/* @todo: Harshit | Replace with the component from roseate */}
          <Progress
            percent={progress}
            status="active"
            style={{ alignItems: 'center', display: 'flex' }}
            strokeColor={theme.colors.primary.DEFAULT}
            showInfo={false}
          />
        </div>
      </div>
      <ChatContainer id="vca-chat-container" className="flex-1">
        <div
          style={{ zoom }}
          className="flex flex-col flex-1 overflow-auto h-full"
        >
          <ChatLayout
            messages={chatMessagesWithLoader}
            roles={chatRoles}
            autoScroll
            renderEmptyContent={false}
            labelRenderer={(label) => {
              return (
                <span className="text-gray-400 px-2 py-1 rounded-md bg-green-100">
                  {label as string}
                </span>
              );
            }}
            avatar={{
              size: 'lg',
            }}
            actionRenderer={actionRenderer}
            contentRenderer={messageRenderer}
          />
        </div>
        <ChatControls ref={chatControlsRef} onSend={handleSendMessage} />
      </ChatContainer>
      <VCALoader loading={loading || loadingForm} step={1} />
      <Forms />
      <CompletionScreen isOpen={complete} />
    </div>
  );
};

export { VCA };
